import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// text-box removes the font's ascent/descent reserve so labels centre on
// their cap-to-baseline band: without it, descender-less labels like "Talk"
// sit visibly high inside the pill while "Workshop" looks centred. The
// property only applies to block containers, which is why the display flips
// to inline-block under the same gate, with icons inline, baseline-aligned,
// and cap-height sized. Everything is gated behind @supports (Chrome 133+/
// Safari 18.2+; Firefox falls back to em-box centring): the py-[4px] keeps
// the pill at the same 22px height as the untrimmed py-0.5 rendering.
// Written out literally (no interpolation) so Tailwind's scanner picks the
// class names up.
const badgeBase =
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-base leading-none font-medium w-fit min-w-0 shrink-0 gap-1 wrap-break-word [&>svg]:size-[1em] [&>svg]:pointer-events-none [a&]:no-underline [a&]:hover:no-underline supports-[text-box:trim-both_cap_alphabetic]:[text-box:trim-both_cap_alphabetic] supports-[text-box:trim-both_cap_alphabetic]:inline-block supports-[text-box:trim-both_cap_alphabetic]:py-[4px] supports-[text-box:trim-both_cap_alphabetic]:[&>svg]:size-[0.75em] supports-[text-box:trim-both_cap_alphabetic]:[&>svg]:mr-1 supports-[text-box:trim-both_cap_alphabetic]:[&>svg]:inline supports-[text-box:trim-both_cap_alphabetic]:[&>svg]:align-baseline";

const badgeVariants = cva(badgeBase, {
  variants: {
    variant: {
      default: "border-transparent bg-foreground text-background",
      secondary: "border-border bg-secondary text-secondary-foreground",
      destructive: "border-transparent bg-red-900 text-white",
      outline: "border-border bg-transparent text-foreground",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const Badge = React.forwardRef<
  HTMLElement,
  React.ComponentProps<"span"> &
    VariantProps<typeof badgeVariants> & { asChild?: boolean }
>(({ className, variant, asChild = false, ...props }, ref) => {
  // Ref is intentionally broad because `asChild` may render a non-span element at runtime.
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      ref={ref}
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
});
Badge.displayName = "Badge";

export { Badge, badgeVariants };
